import type { SDKModule } from '../types';
import { injectStyle } from '../utils/dom';
import { log } from '../utils/logger';

/**
 * Responsive Cleanup Module — neutralizes the backend-injected
 * responsive-page zoom shim on release pages.
 *
 * The inline #responsive-page-js script has already run by the time the
 * SDK (defer) executes: body.style.zoom is set and a closure-scoped
 * resize listener keeps rewriting it. That listener cannot be removed,
 * so instead the injected nodes are removed and a
 * `body{zoom:1 !important}` override is injected — author-stylesheet
 * !important beats non-important inline style, so later fit() calls
 * have no effect.
 *
 * Applies when the URL is a release page OR the document is flagged as
 * webpage-sourced (`<html data-source-from="webpage">`) — the latter covers
 * pages served from custom domains, where the URL says nothing.
 *
 * @param hrefOverride test seam; production uses location.href.
 */
export function createResponsiveCleanup(hrefOverride?: string): SDKModule {
  let overrideStyle: HTMLStyleElement | null = null;

  return {
    name: 'responsive-cleanup',

    init(): void {
      const href = hrefOverride ?? location.href;
      const isRelease = href.indexOf('ptengine.com/release/') !== -1;
      const isWebpageSource =
        document.documentElement?.getAttribute('data-source-from') === 'webpage';
      if (!isRelease && !isWebpageSource) return;

      try {
        document.getElementById('responsive-page')?.remove();
        document.getElementById('responsive-page-js')?.remove();
        if (document.body) document.body.style.zoom = '';
        overrideStyle = injectStyle('body{zoom:1 !important;width:auto !important}');
        overrideStyle.id = 'pt-responsive-cleanup';
      } catch (e) {
        log('warn', 'responsive-cleanup failed', e);
      }
    },

    destroy(): void {
      overrideStyle?.remove();
      overrideStyle = null;
    },
  };
}
